/**
 * AppearanceSettings - Reusable UI component for appearance customization
 * 
 * Features:
 * - Component selector sidebar
 * - Theme, font, accent color controls
 * - Shows current values with visual feedback
 * - Customizable component list
 */

import React, { useState } from 'react';
import { Sun, Moon, Monitor, Check, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useAppearance } from './AppearanceProvider';
import {
    UIComponentConfig,
    DEFAULT_UI_COMPONENTS,
    ACCENT_COLORS,
    FONT_FAMILIES,
    FONT_SIZES,
    Theme,
    FontSize,
    FontFamily,
    AccentColor,
} from './types';

interface AppearanceSettingsProps {
    /** Custom UI components to show in selector. Uses defaults if not provided. */
    components?: UIComponentConfig[];
    /** Optional title override */
    title?: string;
    /** Optional description override */
    description?: string;
    /** Show reset buttons */
    showReset?: boolean;
    /** Compact mode for smaller spaces */
    compact?: boolean;
}

export function AppearanceSettings({
    components = DEFAULT_UI_COMPONENTS,
    title = 'Appearance',
    description = 'Customize the look and feel of your workspace',
    showReset = true,
    compact = false,
}: AppearanceSettingsProps) {
    const appearance = useAppearance();
    const [selectedComponent, setSelectedComponent] = useState<string>('global');

    // Get current settings for selected component
    const currentSettings = appearance.getComponentAppearance(selectedComponent);
    const isGlobal = selectedComponent === 'global';
    const selectedConfig = components.find(c => c.id === selectedComponent);
    const hasOverrides = !isGlobal && appearance.config.components[selectedComponent];

    // Handlers
    const handleThemeChange = (theme: Theme) => {
        if (isGlobal) {
            appearance.setTheme(theme);
        } else {
            appearance.updateComponent(selectedComponent, { theme });
        }
    };

    const handleFontSizeChange = (fontSize: FontSize) => {
        if (isGlobal) {
            appearance.setFontSize(fontSize);
        } else {
            appearance.updateComponent(selectedComponent, { fontSize });
        }
    };

    const handleFontFamilyChange = (fontFamily: FontFamily) => {
        if (isGlobal) {
            appearance.setFontFamily(fontFamily);
        } else {
            appearance.updateComponent(selectedComponent, { fontFamily });
        }
    };

    const handleAccentColorChange = (accentColor: AccentColor) => {
        if (isGlobal) {
            appearance.setAccentColor(accentColor);
        } else {
            appearance.updateComponent(selectedComponent, { accentColor });
        }
    };

    const handleReset = () => {
        if (isGlobal) {
            appearance.resetAll();
        } else {
            appearance.resetComponent(selectedComponent);
        }
    };

    return (
        <div className={`flex flex-col lg:flex-row gap-6 ${compact ? 'gap-4' : ''}`}>
            {/* Component Selector */}
            <div className={`${compact ? 'lg:w-48' : 'lg:w-64'} flex-shrink-0`}>
                <div className="mb-4">
                    <h2 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>Component</h2>
                    <p className="text-sm text-gray-500">Select area to customize</p>
                </div>
                <div className="space-y-1">
                    {components.map((component) => {
                        const hasCustom = appearance.config.components[component.id];
                        return (
                            <button
                                key={component.id}
                                onClick={() => setSelectedComponent(component.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${selectedComponent === component.id
                                        ? 'bg-shiftleft-100 text-shiftleft-700 border border-shiftleft-200'
                                        : 'hover:bg-gray-100 text-gray-700 border border-transparent'
                                    }`}
                            >
                                {component.icon && <component.icon className="h-4 w-4 flex-shrink-0" />}
                                <span className="text-sm font-medium flex-1 truncate">{component.label}</span>
                                {hasCustom && component.id !== 'global' && (
                                    <span className="w-2 h-2 rounded-full bg-shiftleft-500" title="Has custom settings" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Settings Panel */}
            <div className="flex-1">
                <Card className="border-gray-200 bg-white">
                    <CardContent className={compact ? 'p-4' : 'p-6'}>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                {selectedConfig?.icon && (
                                    <div className="p-2 bg-shiftleft-100 rounded-lg text-shiftleft-600">
                                        <selectedConfig.icon className="h-5 w-5" />
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-semibold text-gray-900">{selectedConfig?.label || 'Settings'}</h3>
                                    <p className="text-sm text-gray-500">{selectedConfig?.description}</p>
                                </div>
                            </div>
                            {showReset && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleReset}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <RotateCcw className="h-4 w-4 mr-1" />
                                    {isGlobal ? 'Reset All' : 'Use Global'}
                                </Button>
                            )}
                        </div>

                        {/* Override indicator */}
                        {hasOverrides && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                                This component has custom overrides. Click "Use Global" to reset.
                            </div>
                        )}

                        {/* Settings Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Theme */}
                            <div className="space-y-3">
                                <Label className="text-sm font-medium text-gray-700">Color Theme</Label>
                                <div className="flex gap-2">
                                    <Button
                                        variant={currentSettings.theme === 'light' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handleThemeChange('light')}
                                        className="flex-1"
                                    >
                                        <Sun className="h-4 w-4 mr-1" />
                                        Light
                                    </Button>
                                    <Button
                                        variant={currentSettings.theme === 'dark' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handleThemeChange('dark')}
                                        className="flex-1"
                                    >
                                        <Moon className="h-4 w-4 mr-1" />
                                        Dark
                                    </Button>
                                    <Button
                                        variant={currentSettings.theme === 'system' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => handleThemeChange('system')}
                                        className="flex-1"
                                    >
                                        <Monitor className="h-4 w-4 mr-1" />
                                        Auto
                                    </Button>
                                </div>
                            </div>

                            {/* Accent Color */}
                            <div className="space-y-3">
                                <Label className="text-sm font-medium text-gray-700">Accent Color</Label>
                                <div className="flex gap-2 flex-wrap">
                                    {ACCENT_COLORS.map((color) => (
                                        <button
                                            key={color.value}
                                            onClick={() => handleAccentColorChange(color.value)}
                                            className={`w-8 h-8 rounded-full border-2 transition-all flex items-center justify-center ${currentSettings.accentColor === color.value
                                                    ? 'border-gray-800 scale-110 shadow-md'
                                                    : 'border-white shadow-sm hover:scale-105'
                                                }`}
                                            style={{ backgroundColor: color.color }}
                                            title={color.label}
                                        >
                                            {currentSettings.accentColor === color.value && (
                                                <Check className="h-4 w-4 text-white drop-shadow" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Font Size */}
                            <div className="space-y-3">
                                <Label className="text-sm font-medium text-gray-700">Font Size</Label>
                                <Select
                                    value={currentSettings.fontSize || 'medium'}
                                    onValueChange={(v) => handleFontSizeChange(v as FontSize)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select size" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {FONT_SIZES.map((size) => (
                                            <SelectItem key={size.value} value={size.value}>
                                                {size.label} ({size.css})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Font Family */}
                            <div className="space-y-3">
                                <Label className="text-sm font-medium text-gray-700">Font Family</Label>
                                <Select
                                    value={currentSettings.fontFamily || 'system'}
                                    onValueChange={(v) => handleFontFamilyChange(v as FontFamily)}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select font" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {FONT_FAMILIES.map((font) => (
                                            <SelectItem key={font.value} value={font.value}>
                                                {font.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
